<template>
	<section class="key-details-section">
		<header class="heading">
			<h2>{{ t("contactResearchPage.result.keyDetails") }}</h2>
			<span>{{ t("contactResearchPage.result.keyDetailsDescription") }}</span>
		</header>
		<div class="table">
			<div class="table-header">
				<span>{{ t("contactResearchPage.result.type") }}</span>
				<span>{{ t("contactResearchPage.result.value") }}</span>
				<span>{{ t("contactResearchPage.result.sources") }}</span>
				<span />
			</div>
			<div v-for="detail in details" :key="`${detail.type}-${detail.value}`" class="row">
				<div class="detail-type">
					<span :class="[detail.type, detail.indicator]" />{{ t(`contactResearchPage.result.types.${detail.type}`) }}
				</div>
				<div class="detail-value">
					<strong>{{ detail.value }}</strong>
					<span v-if="detail.bestMatch" class="best-match">{{ t("contactResearchPage.result.bestMatch") }}</span>
				</div>
				<div class="detail-sources">
					<span v-for="source in detail.sources" :key="source" class="detail-source" :class="sourceClass(source)">
						{{ source }}
					</span>
				</div>
				<Button variant="secondary" size="xs" @click="copyDetail(detail.value)">
					<AppIcon :name="IconName.Copy" :size="14" />
					{{ t("contactResearchPage.result.copy") }}
				</Button>
			</div>
			<p v-if="details.length === 0" class="empty-details">{{ t("contactResearchPage.result.noDetails") }}</p>
		</div>
	</section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"
import { useToastStore } from "@/stores/toastStore"
import type { ContactResearchProviderId, ContactResearchProviderInfo } from "@/types/contactResearch"

type KeyContactDetail = {
	type: string
	value: string
	sources: string[]
	bestMatch: boolean
	indicator: string
}

const props = defineProps<{
	details: KeyContactDetail[]
	providers: ContactResearchProviderInfo[]
}>()

const { t } = useI18n()
const toastStore = useToastStore()

function sourceClass(source: string): ContactResearchProviderId | "source-url" {
	const provider = props.providers.find(candidate => candidate.label === source)

	return provider?.id ?? "source-url"
}

async function copyDetail(value: string): Promise<void> {
	await navigator.clipboard.writeText(value)
	toastStore.success("notifications.copySuccess")
}
</script>

<style scoped>
.heading {
	display: flex;
	align-items: baseline;
	gap: var(--space-12);
	margin-bottom: var(--space-12);

	h2,
	span {
		margin: 0;
	}

	h2 {
		font-size: var(--font-size-large);
	}

	span {
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}
}

.table {
	overflow: hidden;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
}

.table-header,
.row {
	display: grid;
	grid-template-columns: 120px minmax(240px, 0.75fr) minmax(280px, 1fr) 84px;
	align-items: center;
	gap: var(--space-16);
	padding: var(--space-12) var(--space-18);
}

.table-header {
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-semibold);
	background: var(--neutral-50);
}

.row {
	min-height: var(--space-64);
	border-top: var(--border-width-thin) solid var(--neutral-200);

	:deep(.btn) {
		gap: var(--space-6);
	}
}

.detail-type {
	display: flex;
	align-items: center;
	gap: var(--space-8);
	color: var(--neutral-600);
	font-size: var(--font-size-small);

	> span {
		width: var(--space-8);
		height: var(--space-8);
		background: var(--neutral-300);
		border: var(--border-width-thin) solid transparent;
		border-radius: var(--border-radius-sm);
		box-sizing: border-box;

		&.green {
			background: var(--primary-green);
		}

		&.blue {
			background: var(--accent-info);
		}

		&.grey {
			background: var(--neutral-600);
		}
	}
}

.detail-value {
	display: grid;
	justify-items: start;
	gap: var(--space-4);
	min-width: 0;

	strong {
		overflow: hidden;
		max-width: 100%;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.best-match {
	padding: var(--space-2) var(--space-6);
	color: var(--primary-green);
	font-size: var(--font-size-xxs);
	font-weight: var(--font-weight-semibold);
	background: color-mix(in srgb, var(--primary-green) 8%, white);
	border-radius: var(--border-radius-full);
}

.detail-sources {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-6);
}

.detail-source {
	display: inline-flex;
	align-items: center;
	min-height: var(--space-24);
	padding: var(--space-3) var(--space-8);
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	background: var(--neutral-100);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-full);

	&.surfe {
		color: var(--primary-green);
		background: color-mix(in srgb, var(--primary-green) 7%, var(--neutral-0));
		border-color: color-mix(in srgb, var(--primary-green) 35%, var(--neutral-0));
	}

	&.webSearch,
	&.source-url {
		color: var(--accent-info);
		background: var(--accent-info-background);
		border-color: color-mix(in srgb, var(--accent-info) 35%, var(--neutral-0));
	}

	&.hunter {
		color: var(--accent-warning);
		background: color-mix(in srgb, var(--accent-warning) 7%, var(--neutral-0));
		border-color: color-mix(in srgb, var(--accent-warning) 25%, var(--neutral-0));
	}

	&.canada411 {
		color: var(--accent-dev-potential-medium);
		background: color-mix(in srgb, var(--accent-dev-potential-medium) 14%, var(--neutral-0));
		border-color: currentColor;
	}
}

.empty-details {
	padding: var(--space-20);
	margin: 0;
	color: var(--neutral-600);
	font-size: var(--font-size-small);
	border-top: var(--border-width-thin) solid var(--neutral-200);
}

@media (max-width: 1080px) {
	.table-header,
	.row {
		grid-template-columns: 100px minmax(200px, 0.75fr) minmax(220px, 1fr) 84px;
	}
}

@media (max-width: 640px) {
	.heading {
		align-items: flex-start;
		flex-direction: column;
		gap: var(--space-4);
	}

	.table-header {
		display: none;
	}

	.row {
		grid-template-columns: minmax(0, 1fr) auto;
		gap: var(--space-10);

		.detail-type,
		.detail-sources {
			grid-column: 1 / -1;
		}
	}
}
</style>
